import { todayPT } from "@homebound/activesupport";
import { Css, type HeaderAction, type MenuItem } from "@homebound/beam";
import { type ReactNode } from "react";
import { pluralize } from "src/utils";
import { formatMonthDayYear } from "src/utils/dates";
import { Temporal } from "temporal-polyfill";

export type CycleTrackingActionProps = {
  completedAt?: Temporal.PlainDate;
  onMarkComplete: () => void;
  onReopen: () => void;
  onReset: () => void;
  onStart: () => void;
  startedAt?: Temporal.PlainDate;
  title: string;
};

/** Always `keepVisible` so PageHeader keeps the chip out of the `sm` overflow menu. */
export type CycleTrackingHeaderAction = Extract<HeaderAction, { kind: "menu" }> & { keepVisible: true };

export function cycleTrackingAction(props: CycleTrackingActionProps): CycleTrackingHeaderAction {
  const { completedAt, onMarkComplete, onReopen, onReset, onStart, startedAt, title } = props;

  if (startedAt && completedAt) {
    return completeAction({ title, startedAt, completedAt, onReopen, onReset });
  }
  if (startedAt) {
    return inProgressAction({ title, startedAt, onMarkComplete, onReset });
  }
  return notStartedAction({ title, onStart });
}

function notStartedAction(props: { onStart: () => void; title: string }): CycleTrackingHeaderAction {
  const { onStart, title } = props;
  return menuAction({
    title,
    statusLabel: "Not Started",
    meta: "No started",
    colorScheme: "neutral",
    items: [{ label: "Start", onClick: onStart }],
  });
}

function inProgressAction(props: {
  onMarkComplete: () => void;
  onReset: () => void;
  startedAt: Temporal.PlainDate;
  title: string;
}): CycleTrackingHeaderAction {
  const { onMarkComplete, onReset, startedAt, title } = props;
  const runningDays = calendarDaysBetween(startedAt, todayPT());
  return menuAction({
    title,
    statusLabel: "In Progress",
    colorScheme: "info",
    meta: `Running: ${runningDays}d`,
    details: `Started ${formatMonthDayYear(startedAt)}`,
    items: [
      { label: "Mark Complete", onClick: onMarkComplete, description: "Records today as the completion date" },
      resetCycleItem(onReset),
    ],
  });
}

function completeAction(props: {
  completedAt: Temporal.PlainDate;
  onReopen: () => void;
  onReset: () => void;
  startedAt: Temporal.PlainDate;
  title: string;
}): CycleTrackingHeaderAction {
  const { completedAt, onReopen, onReset, startedAt, title } = props;
  return menuAction({
    title,
    statusLabel: "Complete",
    colorScheme: "success",
    meta: formatCompletedDuration(startedAt, completedAt),
    details: (
      <>
        Started {formatMonthDayYear(startedAt)}
        <br />
        Completed {formatMonthDayYear(completedAt)}
      </>
    ),
    items: [
      { label: "Reopen", onClick: onReopen, description: "Resumes tracking from original start date." },
      resetCycleItem(onReset),
    ],
  });
}

function menuAction(props: {
  colorScheme: "neutral" | "info" | "success";
  details?: ReactNode;
  items: MenuItem[];
  meta?: string;
  statusLabel: string;
  title: string;
}): CycleTrackingHeaderAction {
  const { colorScheme, details, items, meta, statusLabel, title } = props;
  return {
    kind: "menu",
    keepVisible: true,
    trigger: { label: `${title} · ${statusLabel}`, colorScheme },
    header: createCycleHeader({ title, meta, details }),
    items,
  };
}

function resetCycleItem(onReset: () => void): MenuItem {
  return {
    label: "Reset Cycle",
    onClick: onReset,
    description: "Permanently deletes all timestamps",
    destructive: true,
  };
}

function createCycleHeader(props: { details?: ReactNode; meta?: string; title: string }) {
  const { details, meta, title } = props;
  return (
    <div css={Css.df.fdc.gapPx(4).$}>
      <div css={Css.df.aic.gap1.$}>
        <span css={Css.xsSb.$} data-testid="cycleTrackingTitle">
          {title}
        </span>
        {meta && (
          <span css={Css.xs2.$} data-testid="cycleTrackingMeta">
            {meta}
          </span>
        )}
      </div>
      {details && (
        <div css={Css.xs2.$} data-testid="cycleTrackingDetails">
          {details}
        </div>
      )}
    </div>
  );
}

function formatCompletedDuration(startedAt: Temporal.PlainDate, completedAt: Temporal.PlainDate): string {
  const totalDays = calendarDaysBetween(startedAt, completedAt);
  const weeks = Math.floor(totalDays / 7);
  const days = totalDays % 7;
  if (weeks === 0) return `${totalDays} ${pluralize(totalDays, "day")}`;
  if (days === 0) return `${weeks} ${pluralize(weeks, "Week")}`;
  return `${weeks} ${pluralize(weeks, "Week")}, ${days} ${pluralize(days, "day")}`;
}

function calendarDaysBetween(from: Temporal.PlainDate, to: Temporal.PlainDate): number {
  return from.until(to).total({ unit: "days" });
}
